<template>
  <BaseModal
    :is-open="localeModalStore.showLocaleModal"
    overlay-color="rgba(255, 255, 255, 0.8)"
    @request-close="closeModal"
  >
    <div :class="$style.wrap" data-locale-setter-v="3.1.0">
      <div :class="$style.modal">
        <header :class="$style.header">
          {{ localizedStrings['select-location'] }}
          <button :class="$style.close" @click="closeModal">
            <IconClose width="24" height="24" />
          </button>
        </header>

        <div :class="$style.inner">
          <div key="language">
            <div :class="$style.label">
              {{ localizedStrings['language'] }}
            </div>
            <ClientOnly>
              <BaseSelect
                v-model="language"
                :options="computedLanguages"
                :placeholder="localizedStrings['choose-your-language']"
                :active-filter-value="computedActiveLanguage"
                @on-change="handleLanguageSelected"
              />
            </ClientOnly>
          </div>

          <div key="countries">
            <div :class="[$style.labelWrap, $style.label]">
              {{ computedCountryRegionString }}
            </div>
            <ClientOnly>
              <BaseSelect
                v-model="country"
                :options="computedCountries"
                :placeholder="localizedStrings['choose-your-country']"
                :active-filter-value="computedActiveCountry"
                :searchable="true"
              />
            </ClientOnly>

            <div :class="$style.optionsWrap">
              <BaseButton :class="$style.button" @click="onConfirm">
                {{ localizedStrings['finish'] }}
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { LOCALES } from '~/utils/const'
import {
  getCountrySlugCandidates as getSharedCountrySlugCandidates,
  resolveCountryValue,
} from '~/utils/countrySlug'

const { $client } = useNuxtApp()
const { locale } = useI18n()
const { localizedStrings } = useLocalizedStrings()
const cookieStore = useCookieStore()
const route = useRoute()
const runtimeConfig = useRuntimeConfig()

type IOption = {
  label: string
  value: string
}

type IGeoPayload = {
  country_code?: string
}

declare global {
  interface Window {
    __wpnLocaleDebug?: {
      inspectGeoCountry: (countryCode: string) => Promise<{
        countryCode: string
        candidates: string[]
        match: string
        hasMatch: boolean
      }>
    }
  }
}

const languageOptions: IOption[] = [
  {
    label: '简体中文',
    value: 'zh-Hans',
  },
  {
    label: '繁體中文',
    value: 'zh-Hant',
  },
  {
    label: 'English',
    value: 'en',
  },
  {
    label: 'Français',
    value: 'fr',
  },
  {
    label: 'Deutsch',
    value: 'de',
  },
  {
    label: 'Italiano',
    value: 'it',
  },
  {
    label: '日本語',
    value: 'ja',
  },
  {
    label: '한국어',
    value: 'ko',
  },
  {
    label: 'Português',
    value: 'pt-BR',
  },
  {
    label: 'Русский',
    value: 'ru',
  },
  {
    label: 'Español',
    value: 'es',
  },
]

// Data
const visitorLanguage = useCookie<ILocale>('visitor_language')
const visitorCountry = useCookie<string>('visitor_country')
const initialLanguage =
  LOCALES.find((candidate) => candidate === visitorLanguage.value) ||
  LOCALES.find((candidate) => candidate === locale.value) ||
  'en'
const language = ref<ILocale>(initialLanguage)
const country = ref(cookieStore.countryCookie)
const countries = ref<IOption[]>([])
const loadedCountriesLanguage = ref<ILocale | ''>('')
const geoCountryCode = ref('')
const countriesLoadPromise = ref<Promise<void> | null>(null)
const geoLoadPromise = ref<Promise<void> | null>(null)
const geoLoadAttempted = ref(false)

const resolvePreferredLanguage = (): ILocale => {
  const fallback =
    LOCALES.find((candidate) => candidate === visitorLanguage.value) ||
    LOCALES.find((candidate) => candidate === locale.value) ||
    'en'

  if (import.meta.server) return fallback

  const preferredLanguages = [
    ...(window.navigator.languages || []),
    window.navigator.language,
  ].filter((candidate): candidate is string => !!candidate)

  for (const preferred of preferredLanguages) {
    const normalizedPreferred = preferred.toLowerCase()
    const exactMatch = LOCALES.find(
      (supportedLanguage) =>
        supportedLanguage.toLowerCase() === normalizedPreferred,
    )
    if (exactMatch) return exactMatch

    const baseLanguage = normalizedPreferred.split('-')[0]

    if (baseLanguage === 'zh') {
      const prefersTraditionalChinese =
        normalizedPreferred.includes('hant') ||
        normalizedPreferred.endsWith('-tw') ||
        normalizedPreferred.endsWith('-hk') ||
        normalizedPreferred.endsWith('-mo')

      return prefersTraditionalChinese ? 'zh-Hant' : 'zh-Hans'
    }

    if (baseLanguage === 'pt') return 'pt-BR'

    const baseMatch = LOCALES.find(
      (supportedLanguage) =>
        supportedLanguage.toLowerCase().split('-')[0] === baseLanguage,
    )
    if (baseMatch) return baseMatch
  }

  return fallback
}

const resolveCountryValueFromGeo = (countryOptions: IOption[]): string => {
  return resolveCountryValue(geoCountryCode.value, countryOptions)
}

const getCountrySlugCandidates = (countryCode: string): string[] => {
  return getSharedCountrySlugCandidates(countryCode)
}

const resolveGeoCountryOverride = (): string => {
  if (runtimeConfig.public.ctfHost === 'cdn.contentful.com') return ''

  const geoCountryParam = route.query?.geoCountry
  const geoCountryRaw = Array.isArray(geoCountryParam)
    ? geoCountryParam[0]
    : geoCountryParam

  if (!geoCountryRaw) return ''
  return geoCountryRaw.trim().toUpperCase()
}

if (!visitorLanguage.value) {
  language.value = resolvePreferredLanguage()
}

// Computed
const computedCountryRegionString = computed(
  () =>
    `${localizedStrings.value['country']}/${localizedStrings.value['region']}`,
)

const computedLanguages = computed(() => languageOptions)

const computedActiveLanguage = computed(() => {
  const activeLanguage = language.value || visitorLanguage.value

  return (
    computedLanguages?.value?.find(
      (languageOption) => languageOption?.value === activeLanguage,
    )?.value || ''
  )
})

const computedCountries = computed(() => {
  const sortedCountries = [...countries.value]?.sort?.((a, b) =>
    a?.label > b?.label ? 1 : -1,
  )

  if (!sortedCountries.length) return countries.value

  const organizedCountries = []

  // Put United States at top of list
  const unitedStates = sortedCountries.find(
    (country) => country.value === 'country-united-states',
  )
  if (unitedStates) organizedCountries.push(unitedStates)

  const otherCountries = sortedCountries.filter(
    (country) => country.value !== 'country-united-states',
  )
  if (otherCountries.length) organizedCountries.push(...otherCountries)

  return organizedCountries
})

const computedActiveCountry = computed(() => {
  const activeCountry = country.value || visitorCountry.value

  return (
    computedCountries.value.find(
      (countryOption) => countryOption.value === activeCountry,
    )?.value || ''
  )
})

// Methods
const localeModalStore = useLocalModalStore()

const hideModal = () => {
  localeModalStore.showLocaleModal = false
}

const needsLocalePrefill = computed(
  () => !visitorLanguage.value || !visitorCountry.value,
)

const loadGeoCountryCode = async () => {
  if (geoLoadAttempted.value) return
  if (geoLoadPromise.value) {
    await geoLoadPromise.value
    return
  }

  geoLoadPromise.value = (async () => {
    const geoCountryOverride = resolveGeoCountryOverride()
    if (geoCountryOverride) {
      geoCountryCode.value = geoCountryOverride
      geoLoadAttempted.value = true
      return
    }

    try {
      const geo = await $fetch<IGeoPayload>('/api/geo')
      geoCountryCode.value = geo?.country_code?.toUpperCase() || ''
    } catch {
      geoCountryCode.value = ''
    } finally {
      geoLoadAttempted.value = true
    }
  })()

  try {
    await geoLoadPromise.value
  } finally {
    geoLoadPromise.value = null
  }
}

const loadCountries = async () => {
  const targetLanguage = language.value || locale.value
  if (
    countries.value.length &&
    loadedCountriesLanguage.value === targetLanguage
  ) {
    return
  }
  if (countriesLoadPromise.value) {
    await countriesLoadPromise.value
    if (loadedCountriesLanguage.value === targetLanguage) return
  }

  countriesLoadPromise.value = (async () => {
    const firstPage = await $client.withoutUnresolvableLinks.getEntries({
      content_type: 'locationTag',
      'fields.value[match]': 'country-',
      locale: targetLanguage,
      include: 10,
      limit: 100,
      skip: 0,
    })

    const allCountryItems = [...(firstPage?.items || [])]
    const total = firstPage?.total || 0

    if (total > 100) {
      const remainingRequests = []
      const remainingPageCount = Math.ceil((total - 100) / 100)

      for (let index = 1; index <= remainingPageCount; index++) {
        remainingRequests.push(
          $client.withoutUnresolvableLinks.getEntries({
            content_type: 'locationTag',
            'fields.value[match]': 'country-',
            locale: targetLanguage,
            include: 10,
            limit: 100,
            skip: index * 100,
          }),
        )
      }

      const remainingPayloads = await Promise.all(remainingRequests)
      remainingPayloads.forEach((payload) => {
        if (payload?.items?.length) allCountryItems.push(...payload.items)
      })
    }

    countries.value = allCountryItems.map((tag) => ({
      label: (tag?.fields?.label as string) || '',
      value: (tag?.fields?.value as string) || '',
    }))
    loadedCountriesLanguage.value = targetLanguage
  })()

  try {
    await countriesLoadPromise.value
  } finally {
    countriesLoadPromise.value = null
  }
}

const setupLocaleDebugHelper = () => {
  if (runtimeConfig.public.ctfHost === 'cdn.contentful.com') return
  if (import.meta.server) return

  window.__wpnLocaleDebug = {
    inspectGeoCountry: async (countryCode: string) => {
      await loadCountries()

      const normalizedCountryCode = countryCode.trim().toUpperCase()
      const candidates = getCountrySlugCandidates(normalizedCountryCode)
      const match =
        candidates.find((candidate) =>
          countries.value.some((option) => option.value === candidate),
        ) || ''

      return {
        countryCode: normalizedCountryCode,
        candidates,
        match,
        hasMatch: !!match,
      }
    },
  }
}

const persistPreferences = async ({
  nextCountry = country.value,
  nextLanguage = language.value,
  switchLocale = false,
}: {
  nextCountry?: string
  nextLanguage?: ILocale
  switchLocale?: boolean
} = {}) => {
  try {
    const baseUrl = window.location.origin || runtimeConfig.public.baseUrl
    await $fetch(`${baseUrl}/api/user-preferences`, {
      method: 'post',
      body: { country: nextCountry, language: nextLanguage },
    })

    cookieStore.languageCookie = nextLanguage
    cookieStore.countryCookie = nextCountry
    visitorLanguage.value = nextLanguage
    visitorCountry.value = nextCountry
    language.value = nextLanguage
    country.value = nextCountry

    window.dispatchEvent(
      new CustomEvent('wpn:visitor-preferences-updated', {
        detail: {
          country: nextCountry,
          language: nextLanguage,
        },
      }),
    )

    hideModal()
  } catch (e) {
    // TODO: Display some error
    console.error(e)
    window.location.href = '/'
    return
  }

  const switchLocalePath = useSwitchLocalePath()
  const nextUrl = switchLocale
    ? switchLocalePath(nextLanguage)
    : window.location.href

  window.location.assign(nextUrl)
}

const closeModal = async () => {
  if (!needsLocalePrefill.value) {
    hideModal()
    return
  }

  const nextLanguage =
    language.value || visitorLanguage.value || resolvePreferredLanguage()

  let nextCountry = country.value || visitorCountry.value || ''

  if (!nextCountry) {
    await Promise.all([loadGeoCountryCode(), loadCountries()])
    nextCountry = resolveCountryValueFromGeo(countries.value)
  }

  await persistPreferences({
    nextCountry,
    nextLanguage,
    switchLocale: false,
  })
}

const handleLanguageSelected = async () => {
  await Promise.all([loadGeoCountryCode(), loadCountries()])

  if (!country.value) {
    country.value = resolveCountryValueFromGeo(countries.value)
  }
}

watch(
  () => localeModalStore.showLocaleModal,
  async (isOpen) => {
    if (!isOpen) return

    await handleLanguageSelected()
  },
  { immediate: true },
)

onMounted(() => {
  setupLocaleDebugHelper()
})

const onConfirm = async () => {
  await persistPreferences({ switchLocale: true })
}
</script>

<style lang="scss" module>
.wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 20px;
}

.modal {
  display: block;
  background: #fff;
  box-shadow: 0 4px 13px rgba(#000, 0.06);
  max-width: 336px;
  width: 100%;
}

.header {
  display: grid;
  grid-template-columns: auto auto;
  gap: 5px;
  align-items: center;
  justify-content: space-between;
  background: var(--purple-dark);
  padding: 2px 0 0 20px;
  color: #fff;
  font-family: var(--roboto-condensed);
  font-size: to-rem(23px);
  line-height: math.div(46px, 23px);
}

.close {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: 48px;
  height: 48px;
  border: 0;
  background: none;
  color: #fff;
}

.inner {
  border: solid var(--gray-4);
  border-width: 0 1px 1px;
  padding: 20px;
}

.label {
  margin-bottom: 14px;
  font-size: to-rem(20px);
  line-height: math.div(27px, 20px);
  color: #000;
  font-weight: 700;
}

.labelWrap {
  margin-top: 14px;
}

.optionsWrap {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 35px;
}

.button.button {
  --button-min-width: 50%;
}
</style>
